import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
} from "lucide-react";
import { RowCards } from "@/components/responsive-table";
import { useIsSmUp } from "@/lib/use-media-query";

export interface DataTableColumn<T> {
  /** Stable id; also the server ?sort= value when `sortable`. */
  key: string;
  header: ReactNode;
  /** Enable click-to-sort on this column header. */
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  /**
   * 185: card-mode hint for the below-`sm` reflow (desktop table unaffected).
   * "title" -> card heading (defaults to first column), "action" -> grouped
   * top-right, "hidden" -> omitted from the card. See `responsive-table.tsx`.
   */
  cardRole?: "title" | "action" | "hidden";
  render: (row: T) => ReactNode;
}

export interface DataTableFetchParams {
  page: number;
  per_page: number;
  search: string | undefined;
  sort: string | undefined;
  order: "asc" | "desc";
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  /** Server-driven fetch. Re-invoked whenever a query param or `fetchKey` changes. */
  fetcher: (params: DataTableFetchParams) => Promise<{ data: T[]; total: number }>;
  rowKey: (row: T) => string;
  /**
   * Changes to this string force a refetch (e.g. when a page-level filter such
   * as the active school scope changes). Keep it stable otherwise.
   */
  fetchKey?: string;
  searchPlaceholder?: string;
  /** Page-specific filter controls rendered in the toolbar next to the search box. */
  toolbarExtra?: ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  /** Extra attributes (e.g. data-item / data-item-id) attached to each row. */
  rowProps?: (row: T) => Record<string, string | undefined>;
  /**
   * 186: optional bespoke mobile card (below `sm`). When provided it replaces the
   * generic `RowCards` label:value reflow with a purpose-built card per row
   * (e.g. the prototype's date-block event card). Desktop table is unaffected.
   * The renderer owns its own navigation/`data-*`; DataTable still owns
   * fetch/search/pagination/state.
   */
  renderCard?: (row: T) => ReactNode;
  pageSize?: number;
  emptyMessage?: string;
  /** Default sort column key applied when the URL has none. */
  defaultSort?: string;
  defaultOrder?: "asc" | "desc";
  /** Reports the table's lifecycle state (loading | error | empty | ready). */
  onStateChange?: (state: "loading" | "error" | "empty" | "ready") => void;
}

const DEFAULT_PAGE_SIZE = 20;

export interface ClientListConfig<T> {
  /** Fields concatenated for the search match (case-insensitive substring). */
  searchFields?: (row: T) => (string | null | undefined)[];
  /** Sort key -> value accessor. Strings sort with localeCompare, numbers numerically. */
  sortAccessors?: Record<string, (row: T) => string | number | null | undefined>;
  defaultSort?: string;
}

/**
 * Apply search/sort/pagination to an in-memory array, returning the
 * { data, total } shape a DataTable `fetcher` expects. Use this as a fallback
 * when a fetcher must merge multiple server sources (e.g. several pinned
 * schools) or apply a page-local filter (e.g. pin filtering) that the server
 * endpoint does not model. Prefer pure server-side fetching where possible.
 */
export function applyClientListParams<T>(
  rows: T[],
  params: DataTableFetchParams,
  config: ClientListConfig<T> = {},
): { data: T[]; total: number } {
  let result = rows;

  const term = params.search?.trim().toLowerCase();
  if (term && config.searchFields) {
    result = result.filter((row) =>
      config.searchFields!(row).some((f) => (f ?? "").toLowerCase().includes(term)),
    );
  }

  const sortKey = params.sort ?? config.defaultSort;
  const accessor = sortKey ? config.sortAccessors?.[sortKey] : undefined;
  if (accessor) {
    result = [...result].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      let cmp: number;
      if (typeof av === "number" || typeof bv === "number") {
        cmp = Number(av ?? 0) - Number(bv ?? 0);
      } else {
        cmp = String(av ?? "").localeCompare(String(bv ?? ""));
      }
      return params.order === "desc" ? -cmp : cmp;
    });
  }

  const total = result.length;
  const start = (params.page - 1) * params.per_page;
  return { data: result.slice(start, start + params.per_page), total };
}

export function DataTable<T>({
  columns,
  fetcher,
  rowKey,
  fetchKey = "",
  searchPlaceholder = "Search...",
  toolbarExtra,
  onRowClick,
  rowClassName,
  rowProps,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyMessage = "No results.",
  defaultSort,
  defaultOrder = "asc",
  onStateChange,
  renderCard,
}: DataTableProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const smUp = useIsSmUp();

  const q = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? defaultSort ?? "";
  const order: "asc" | "desc" = (searchParams.get("order") ?? defaultOrder) === "desc" ? "desc" : "asc";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Local search text decoupled from the URL so typing stays responsive; the
  // URL `q` (and thus the fetch) updates on a debounce.
  const [searchText, setSearchText] = useState(q);
  useEffect(() => { setSearchText(q); }, [q]);

  // Keep the latest fetcher closure without making it a fetch dependency (the
  // page may recreate it every render). `fetchKey` is the intentional trigger.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Mutate a single URL param while preserving the rest.
  const patchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v === null || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Debounce search -> URL.
  useEffect(() => {
    if (searchText === q) return;
    const t = setTimeout(() => {
      patchParams({ q: searchText || null, page: null });
    }, 300);
    return () => clearTimeout(t);
  }, [searchText, q, patchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef
      .current({
        page,
        per_page: pageSize,
        search: q || undefined,
        sort: sort || undefined,
        order,
      })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load");
        setRows([]);
        setTotal(0);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, sort, order, page, pageSize, fetchKey]);

  // Report lifecycle state to the host page (drives the page's data-state attr,
  // which the E2E suite asserts on).
  const state: "loading" | "error" | "empty" | "ready" =
    loading ? "loading" : error ? "error" : rows.length === 0 ? "empty" : "ready";
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  useEffect(() => { onStateChangeRef.current?.(state); }, [state]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const fromRow = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const toRow = Math.min(safePage * pageSize, total);

  function toggleSort(key: string) {
    if (sort === key) {
      patchParams({ order: order === "asc" ? "desc" : "asc", page: null });
    } else {
      patchParams({ sort: key, order: "asc", page: null });
    }
  }

  function SortIcon({ columnKey }: { columnKey: string }) {
    if (sort !== columnKey) return <ChevronsUpDown className="h-3 w-3 opacity-40" />;
    return order === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  }

  const colCount = useMemo(() => columns.length, [columns]);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          placeholder={searchPlaceholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="h-8 text-sm sm:max-w-xs"
          data-field="search"
        />
        {toolbarExtra}
      </div>

      {/* Render ONE tree: table on sm+, cards below (185). Both-in-DOM would
          double every row's data-item and break item-click selectors. */}
      {smUp ? (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={`${col.headerClassName ?? ""} ${col.sortable ? "cursor-pointer select-none" : ""}`}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                >
                  {col.sortable ? (
                    <span className="flex items-center gap-1">{col.header} <SortIcon columnKey={col.key} /></span>
                  ) : (
                    col.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-sm text-muted-foreground py-6 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-sm text-destructive py-6 text-center">
                  {error}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} className="text-sm text-muted-foreground py-6 text-center">
                  {q ? "No results match your search." : emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  className={`${onRowClick ? "cursor-pointer" : ""} ${rowClassName?.(row) ?? ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  {...(rowProps?.(row) ?? {})}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} className={col.className}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      ) : (
      <div>
        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading...</p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {q ? "No results match your search." : emptyMessage}
          </p>
        ) : renderCard ? (
          <div className="flex flex-col gap-2.5">
            {rows.map((row) => (
              <div key={rowKey(row)} {...(rowProps?.(row) ?? {})}>
                {renderCard(row)}
              </div>
            ))}
          </div>
        ) : (
          <RowCards
            columns={columns}
            rows={rows}
            rowKey={rowKey}
            rowProps={rowProps}
            onRowClick={onRowClick}
            rowClassName={rowClassName}
          />
        )}
      </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Showing {fromRow}-{toRow} of {total}</span>
          <div className="flex gap-1">
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={safePage <= 1}
              onClick={() => patchParams({ page: String(safePage - 1) })}
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="flex items-center px-2">Page {safePage} of {totalPages}</span>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={safePage >= totalPages}
              onClick={() => patchParams({ page: String(safePage + 1) })}
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
